import { Component } from '@theme/js-component';
import { debounce, requestIdleCallback } from '@theme/js-utilities';

/**
 * A custom element that manages the dialog + dialog trigger relationship for anchoring.
 * Calculates the trigger position and inlines custom properties on the dialog element
 * that can be consumed by CSS for positioning.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dialog – The dialog element.
 * @property {HTMLElement} trigger – The dialog trigger element.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <anchored-dialog-component data-close-on-resize>
 *   <button data-ref="trigger" popovertarget="menu">Open Menu</button>
 *   <div data-ref="dialog" id="menu" popover>Menu content</div>
 * </anchored-dialog-component>
 * ```
 *
 * @property {string[]} requiredRefs - Required refs: 'dialog' and 'trigger'
 * @property {number} [interaction_delay] - The delay in milliseconds for the hover interaction
 * @property {string} [data-close-on-resize] - When present, closes dialog on window resize
 * @property {string} [data-hover-triggered] - When present, makes the dialog function via pointerenter/leave
 * @property {number | null} [dialogTrigger] - The timeout for the dialog trigger
 */
export class AnchoredDialogComponent extends Component {
    requiredRefs = ['dialog', 'trigger'];
    interaction_delay = 200;
    #dialogTrigger = /** @type {number | null} */ (null);

    #onTriggerEnter = () => {
        const {trigger, dialog} = this.refs;

        trigger.dataset.hoverActive = 'true';

        if (!dialog.matches(':popover-open')) {
            this.#dialogTrigger = setTimeout(() => {
                if (trigger.matches('[data-hover-active]')) {
                    dialog.showPopover();
                }
            }, this.interaction_delay);
        }
    };

    #onTriggerLeave = () => {
        const {trigger, dialog} = this.refs;
        delete trigger.dataset.hoverActive;

        if (this.#dialogTrigger) {
            clearTimeout(this.#dialogTrigger);
        }

        if (dialog.matches(':popover-open')) {
            this.#dialogTrigger = setTimeout(() => {
                dialog.hidePopover();
            }, this.interaction_delay);
        }
    };

    #onDialogEnter = () => {
        if (this.#dialogTrigger) {
            clearTimeout(this.#dialogTrigger);
        }
    };

    #onDialogLeave = () => {
        const {dialog} = this.refs;

        this.#dialogTrigger = setTimeout(() => {
            dialog.hidePopover();
        }, this.interaction_delay);
    };

    /**
     * Updates the dialog position by calculating trigger element bounds
     * and setting CSS custom properties on the dialog element.
     */
    #updatePosition = async () => {
        const {dialog, trigger} = this.refs;

        if (!dialog || !trigger) {
            return;
        }

        const positions = trigger.getBoundingClientRect();

        dialog.style.setProperty('--anchor-top', `${positions.top}`);
        dialog.style.setProperty('--anchor-right', `${window.innerWidth - positions.right}`);
        dialog.style.setProperty('--anchor-bottom', `${window.innerHeight - positions.bottom}`);
        dialog.style.setProperty('--anchor-left', `${positions.left}`);
        dialog.style.setProperty('--anchor-height', `${positions.height}`);
        dialog.style.setProperty('--anchor-width', `${positions.width}`);
    };

    /**
     * Debounced resize handler that optionally closes the dialog
     * when the window is resized, based on the data-close-on-resize attribute.
     */
    #resizeListener = debounce(() => {
        const dialog = /** @type {HTMLElement} */ (this.refs.dialog);

        if (dialog && dialog.matches(':popover-open')) {
            dialog.hidePopover();
        }
    }, 100);

    /**
     * Component initialization - sets up event listeners for resize and dialog toggle events.
     */
    connectedCallback() {
        super.connectedCallback();
        const {dialog, trigger} = this.refs;

        if (this.dataset.closeOnResize) {
            dialog.addEventListener('beforetoggle', (event) => {
                const evt = /** @type {ToggleEvent} */ (event);
                window[evt.newState === 'open' ? 'addEventListener' : 'removeEventListener']('resize', this.#resizeListener);
            });
        }

        if (this.dataset.hoverTriggered) {
            trigger.addEventListener('pointerenter', this.#onTriggerEnter);
            trigger.addEventListener('pointerleave', this.#onTriggerLeave);
            dialog.addEventListener('pointerenter', this.#onDialogEnter);
            dialog.addEventListener('pointerleave', this.#onDialogLeave);
        }

        if (!CSS.supports('position-anchor: --trigger')) {
            dialog.addEventListener('beforetoggle', () => {
                this.#updatePosition();
            });

            requestIdleCallback(() => {
                this.#updatePosition();
            });
        }
    }

    /**
     * Component cleanup - removes resize event listener.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.#resizeListener);
    }
}

if (!customElements.get('anchored-dialog-component')) {
    customElements.define('anchored-dialog-component', AnchoredDialogComponent);
}
